import type { SiteChatAttachment, SiteChatConfig, SiteChatMessage } from '../types';
import type { SiteChatDevice } from './device';

export type SessionResponse = {
    token: string;
    expiresAt: string;
    visitorId: string;
    channelName: string;
    widget: Partial<SiteChatConfig> | null;
    serverTime: string;
    online: boolean;
    formRequired: boolean;
    leadSubmitted: boolean;
    lead: Record<string, string> | null;
    maxFileSize: number;
    unread: number;
    /** An operator has read the visitor's messages up to this index; -1 when none has. */
    readIndex?: number;
    hasMore: boolean;
    messages: SiteChatMessage[];
    /** A School session: the signed-in student's single thread. */
    school?: boolean;
};

export type LeadResponse = {
    leadSubmitted: boolean;
    errors?: Record<string, string> | null;
};

/** A refusal from AccelChat: `code` is stable (`forbidden`, `not_found`, `lead_required`…); `network` means no answer. */
export class SiteChatError extends Error {
    constructor(public code: string, public status: number) {
        super(code);
    }
}

export class SiteChatApi {
    constructor(private baseUrl: string, private getToken: () => Promise<string | null>) { }

    get hubUrl() {
        return `${this.baseUrl}/site-hub`;
    }

    startSession(body: { channel: string; visitorId: string | null; url: string; referrer: string; lang: string }) {
        return this.request<SessionResponse>('POST', '/api/v1/site-chat/session', body, false);
    }

    /**
     * The membership area's chat: trades the student's School token for a visitor token of the school's
     * membership chat. 404 = the school has none, and School shows its old chat.
     */
    startSchoolSession(schoolToken: string) {
        return this.request<SessionResponse>('POST', '/api/v1/site-chat/school/session', {}, false, schoolToken);
    }

    /**
     * The admin panel's support chat: trades a school admin's platform token for a visitor token of AXL's own
     * support channel. 404 = not configured, and the admin panel keeps its old support chat.
     */
    startAdminSession(adminToken: string, body: { url: string; lang: string }) {
        return this.request<SessionResponse>('POST', '/api/v1/site-chat/admin/session', body, false, adminToken);
    }

    messages(before?: number) {
        return this.request<{ hasMore: boolean; readIndex?: number; messages: SiteChatMessage[] }>('GET', `/api/v1/site-chat/messages${before != null ? `?before=${before}` : ''}`);
    }

    send(body: { clientId: string; text: string; fileIds: string[]; voice?: boolean; device?: SiteChatDevice; url?: string; data?: Record<string, string> }) {
        return this.request<SiteChatMessage>('POST', '/api/v1/site-chat/messages', body);
    }

    async lead(body: { values: Record<string, string>; url: string; device?: SiteChatDevice; data?: Record<string, string> }): Promise<LeadResponse> {
        try {
            return await this.request<LeadResponse>('POST', '/api/v1/site-chat/lead', body);
        } catch (e) {
            if (e instanceof SiteChatError && e.status === 400 && (e as SiteChatError & { body?: LeadResponse }).body)
                return (e as SiteChatError & { body: LeadResponse }).body;
            throw e;
        }
    }

    read(index: number) {
        return this.request<unknown>('POST', '/api/v1/site-chat/read', { index });
    }

    button(messageId: string, index: number) {
        return this.request<unknown>('POST', '/api/v1/site-chat/button', { messageId, index });
    }

    /**
     * The in-app chat's way: AccelChat opens an S3 multipart upload and hands out pre-signed part URLs, the
     * browser PUTs the parts straight to S3 and completes the file with their ETags. The bytes never pass
     * through AccelChat. S3 must answer PUT from any origin and expose `ETag` (bucket CORS).
     */
    async upload(file: File, onProgress: (percent: number) => void, voice?: { meta: string }): Promise<SiteChatAttachment> {
        const begin = await this.request<{ id: string; chunkSize: number; urls: string[] }>('POST', '/api/v1/site-chat/files/begin', {
            name: file.name,
            size: file.size,
            type: file.type || 'application/octet-stream',
            voice: !!voice,
            meta: voice?.meta ?? null,
        });

        const loaded = begin.urls.map(() => 0);
        const eTags: { partNumber: number; eTag: string }[] = [];
        try {
            for (let i = 0; i < begin.urls.length; i++) {
                const part = file.slice(i * begin.chunkSize, (i + 1) * begin.chunkSize);
                const eTag = await this.putPart(begin.urls[i], part, bytes => {
                    loaded[i] = bytes;
                    onProgress(Math.min(99, Math.round(loaded.reduce((sum, x) => sum + x, 0) / file.size * 100)));
                });
                eTags.push({ partNumber: i + 1, eTag });
            }
        } catch (e) {
            // Frees the S3 upload; the chip's «retry» begins a new one.
            void this.request('POST', '/api/v1/site-chat/files/abort', { id: begin.id }).catch(() => undefined);
            throw e;
        }

        return await this.request<SiteChatAttachment>('POST', '/api/v1/site-chat/files/complete', { id: begin.id, eTags });
    }

    /** XHR, not fetch: the chip shows upload progress. A part is tried three times, as the in-app chat does. */
    private async putPart(url: string, part: Blob, onProgress: (bytes: number) => void): Promise<string> {
        let error: SiteChatError = new SiteChatError('upload_failed', 0);
        for (let attempt = 0; attempt < 3; attempt++) {
            try {
                return await new Promise<string>((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('PUT', url);
                    xhr.upload.onprogress = e => onProgress(e.loaded);
                    xhr.onload = () => {
                        // Null when the bucket's CORS does not expose the header: the upload cannot be completed.
                        const eTag = xhr.getResponseHeader('ETag');
                        if (xhr.status >= 200 && xhr.status < 300 && eTag)
                            resolve(eTag);
                        else
                            reject(new SiteChatError('upload_failed', xhr.status));
                    };
                    xhr.onerror = () => reject(new SiteChatError('network', 0));
                    xhr.send(part);
                });
            } catch (e) {
                error = e as SiteChatError;
                onProgress(0);
            }
        }
        throw error;
    }

    private async request<T>(method: string, path: string, body?: unknown, auth = true, bearer?: string): Promise<T> {
        const headers: Record<string, string> = {};
        if (body !== undefined)
            headers['Content-Type'] = 'application/json';
        if (bearer)
            headers.Authorization = `Bearer ${bearer}`;
        else if (auth) {
            const token = await this.getToken();
            if (token)
                headers.Authorization = `Bearer ${token}`;
        }

        let response: Response;
        try {
            response = await fetch(`${this.baseUrl}${path}`, {
                method,
                headers,
                body: body !== undefined ? JSON.stringify(body) : undefined,
                credentials: 'omit',
                mode: 'cors',
            });
        } catch {
            throw new SiteChatError('network', 0);
        }

        const text = await response.text();
        let json: unknown = null;
        try {
            json = text ? JSON.parse(text) : null;
        } catch {
            json = null;
        }
        if (!response.ok) {
            const error = new SiteChatError((json as { code?: string } | null)?.code ?? 'http_' + response.status, response.status) as SiteChatError & { body?: unknown };
            error.body = json;
            throw error;
        }
        return json as T;
    }
}
